import { css } from '@emotion/react';
import { ObjectType, useSearchAuditEvents } from '@frontend/api-phone-audits';
import { useTranslation } from '@frontend/i18n';
import { ModalControlResponse, SpinningLoader, Text, Tray, WIDTH_DIMS } from '@frontend/design-system';
import { EmptyStateSection } from '../../call-routes/empty-state-section';
import { EventDetailsList } from './event-list/event-details-list';

const DESIGN_WIDTH: keyof typeof WIDTH_DIMS = 'smallXL';
export const HISTORY_TRAY_WIDTH = WIDTH_DIMS[DESIGN_WIDTH] ?? 400;

type HistoryDetailsTrayProps = {
  HeaderComponent?: React.ReactNode;
  tenantId: string;
  callObjectId?: string;
  callObjectType?: ObjectType;
  auditEventId?: string;
  modalControls: ModalControlResponse;
  mountTarget?: string;
};

export const HistoryDetailsTray: React.FC<HistoryDetailsTrayProps> = ({
  HeaderComponent,
  tenantId,
  callObjectId,
  callObjectType,
  auditEventId,
  modalControls,
  mountTarget = '[data-settings-modal-content]',
}) => {
  const { t } = useTranslation('phone');

  const { data, isLoading } = useSearchAuditEvents(
    {
      tenantId,
      objectId: callObjectId,
      objectTypes: callObjectType ? [callObjectType] : undefined,
    },
    {
      retry: false,
      enabled: (!!callObjectId || !!callObjectType) && !!tenantId,
    }
  );

  const auditEvents = data?.auditEvents ?? [];

  return (
    <Tray
      mountTarget={mountTarget}
      {...modalControls.modalProps}
      showBackdrop={false}
      // IMPORTANT: the width of the tray must match the HISTORY_TRAY_WIDTH constant above which is used to calculate
      // the width of the different settings pages when the tray is open. If changing the width here, make sure to
      // update the HISTORY_TRAY_WIDTH constant above.
      width={DESIGN_WIDTH}
      css={css`
        padding: var(--fabric-space-gap-md);
      `}
    >
      <section
        css={css`
          display: flex;
          flex-direction: column;
          height: 100%;
        `}
      >
        {HeaderComponent}
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            ${isLoading || auditEvents.length === 0
              ? 'align-items: center; justify-content: center;'
              : 'padding-top: var(--fabric-space-gap-md);'}
          `}
        >
          {isLoading ? (
            <SpinningLoader />
          ) : auditEvents.length > 0 ? (
            <EventDetailsList events={auditEvents} activeEventId={auditEventId} />
          ) : (
            <EmptyStateSection title={t('No history found')} />
          )}
        </div>
        <div
          css={css`
            flex-shrink: 0;
            padding-top: var(--fabric-space-gap-sm);
          `}
        >
          {callObjectId && (
            <Text size='small' color='light' textAlign='right'>
              {t('Object ID: {{id}}', { id: callObjectId })}
            </Text>
          )}
        </div>
      </section>
    </Tray>
  );
};
